import { Injectable } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { TovpilCrudService } from './tovpil-crud-service';
import { CookieService } from 'ngx-cookie-service';
import { CookieTovpil, StoreSelected } from '../shared/models/CookieTovpil';
import { TovpilCookieService } from './tovpil-cookie.service';
import { PeriodSelected } from '../shared/models/SecurityDocument';
import { Constants } from '../shared/vo/Constants';

@Injectable({
    providedIn: 'root'
})
export class UserPreferencesService {

    endPointSaveUserPreferences = "updateSecurityById";
    endPointGetUserPreferences = "findSecurityById";

    constructor(private tovpilCrudService: TovpilCrudService, private tovpilCookieService: TovpilCookieService, private cookieService: CookieService) { }

    // METODOS DE CONVERSION
    /**
     * The function `coordinationListToString` takes an array of `MenuItem` objects, extracts their `id`
     * properties, and returns a string of comma-separated `id` values.
     * @param {MenuItem[]} itemsNavigation - `itemsNavigation` is an array of `MenuItem` objects
     * representing items in a navigation menu. Each `MenuItem` object has an `id` property that is used to
     * identify the item.
     * @returns The `coordinationListToString` function returns a string that contains the IDs of the items
     * in the `itemsNavigation` array joined together with commas.
     */
    coordinationListToString(itemsNavigation: MenuItem[]): string {
        const coordinationLabels = itemsNavigation.map(item => item.id);
        return coordinationLabels.join(',');
    }

    /* The above TypeScript code defines a function `stringToStoreSelectedList` that takes a string as
    input and returns an array of objects of type `StoreSelected`. */
    stringToStoreSelectedList = (str: string): StoreSelected[] => {
        const ids: string[] = str.split(',');
        const storeSelectedList: StoreSelected[] = ids.map(id => {
            return { id };
        });
        return storeSelectedList;
    };

    /**
     * The function `coordinationStringToArray` takes a string of comma-separated values and returns an
     * array of objects with each value as the `id` property.
     * @param {string} coordinationString - The `coordinationStringToArray` function takes a string as
     * input, where the string contains comma-separated values. It then splits the string by commas, trims
     * any extra whitespace from each value, and returns an array of objects with each value as the `id`
     * property.
     * @returns An array of objects, where each object has a property "id" with the value being a trimmed
     * string from the input coordinationString.
     */
    coordinationStringToArray(coordinationString: string): { id: string }[] {
        const values: string[] = coordinationString.split(',');
        return values.map((value) => ({ id: value.trim() }));
    }

    //  OBTIENE Y/O CREA PREFERENCIAS EN BASE DE DATOS 
    /**
     * This TypeScript function asynchronously saves and updates user preferences using provided parameters
     * and returns a boolean based on the success of the operation.
     * @param {string} _storeId - _storeId: The unique identifier for the store or user for whom the
     * preferences are being saved.
     * @param {string} _languageId - The `_languageId` parameter in the `saveUpdateUserPreferences`
     * function is used to specify the language ID that the user prefers. This could be a unique identifier
     * for the selected language in the user's preferences.
     * @param {string} _currencyId - _currencyId is a string representing the unique identifier for the
     * currency selected by the user.
     * @param {string} _coordinationId - The `_coordinationId` parameter in the `saveUpdateUserPreferences`
     * function is used to store the ID related to coordination preferences for a user. It is one of the
     * parameters required for updating user preferences in the system.
     * @param {string} _coordinationStack - The `_coordinationItems` parameter in the
     * `saveUpdateUserPreferences` function is a string that contains multiple items separated by commas.
     * It is used to store a list of coordination items selected by the user.
     * @param {string} _periodYear - The `_periodYear` parameter in the `saveUpdateUserPreferences`
     * function is used to specify the year for the user preferences being saved or updated. It is a string
     * parameter that represents the year value.
     * @param {string} _periodMonth - The `_periodMonth` parameter in the `saveUpdateUserPreferences`
     * function represents the month for which the user preferences are being saved or updated. It is a
     * string parameter that should contain the month value, typically in the format of a numeric string
     * (e.g., "01" for January, "
     * @returns The `saveUpdateUserPreferences` function returns a boolean value. It returns `true` if the
     * user preferences were successfully saved and `false` if there was an error during the process.
     */
    async saveUpdateUserPreferences(_userId: string, _storeId: string, _userDocId: string, _languageId: string, _currencyId: string, _coordinationId: string,
        _coordinationStack: string, _periodYear: string, _periodMonth: string, _device: string) {

        try {
            let params = {
                "id": _userDocId,
                "userId": _userId,
                "guideId": "",
                "fullName": null,
                "usrSystemId": null,
                "supplId": null,
                "functionId": "",
                "levelId": null,
                "storeId": _storeId,
                "lstStores": null,
                "language": _languageId,
                "region": null,
                "stateId": null,
                "currDate": null,
                "strLocal": null,
                "strNatio": null,
                "strZone": null,
                "selYear": _periodYear,
                "selPeriod": _periodMonth,
                "selCurrency": _currencyId,
                "tokenProfile": null,
                "coordinationId": _coordinationId,
                "coordinationStack": _coordinationStack,
                "device": _device,
                "stores": null,
                "storeSelected": null,
                "periodSelected": null,
            }
            const result = await this.tovpilCrudService.fetch(this.endPointSaveUserPreferences, params);
            //console.debug("Debug > User Preferences Response", result);

            if (result.success) {
                return true;
            } else {
                console.error("An error occurred while fetching purchase order data:");
                return false;
            }

        } catch (error) {
            console.error(error);
            return false;
        }
    }

    /**
     * This TypeScript function asynchronously fetches user preferences based on a store ID using a CRUD
     * service.
     * @param {string} _storeId - The `_storeId` parameter is a string representing the unique identifier
     * of a store for which user preferences are being retrieved.
     * @returns The `getUserPreferences` function returns the user preferences data fetched from the server
     * if the operation is successful. If there is an error during the fetch operation or if the result is
     * not successful, it returns `null`.
     */
    async getUserPreferences(_userId: string, _storeId: string, _userDocId: string, _languageId: string, _currencyId: string, _coordinationId: string,
        _coordinationStack: string, _periodYear: string, _periodMonth: string, _device: string) {

        try {
            let params = {
                "id": _userDocId,
                "userId": _userId,
                "guideId": "",
                "fullName": null,
                "usrSystemId": null,
                "supplId": null,
                "functionId": "",
                "levelId": null,
                "storeId": _storeId,
                "lstStores": null,
                "language": _languageId,
                "region": null,
                "stateId": null,
                "currDate": null,
                "strLocal": null,
                "strNatio": null,
                "strZone": null,
                "selYear": _periodYear,
                "selPeriod": _periodMonth,
                "selCurrency": _currencyId,
                "tokenProfile": null,
                "coordinationId": _coordinationId,
                "coordinationStack": _coordinationStack,
                "device": _device,
                "stores": null,
                "storeSelected": null,
                "periodSelected": null,
            }

            const result = await this.tovpilCrudService.fetch(this.endPointGetUserPreferences, params);
            //console.debug("Debug > User Preferences Response", result);

            if (result.success) {
                return result.data;
            } else {
                console.error("An error occurred while fetching purchase order data:");
                return null;
            }

        } catch (error) {
            console.error(error);
            return null;
        }
    }

    /**
     * The function `manageCookie` in TypeScript asynchronously saves or updates user preferences and
     * returns the user preferences if saved successfully.
     * @param {string} storeId - The `storeId` parameter is used to identify the specific store for which
     * the user preferences are being managed.
     * @param {string} languageId - The `languageId` parameter in the `manageCookie` function represents
     * the language preference of the user. It is used to specify the language in which the user prefers to
     * view content or interact with the application.
     * @param {string} currencyId - The `currencyId` parameter in the `manageCookie` function represents
     * the unique identifier for the currency preference of a user. It is used to specify the currency in
     * which prices and transactions are displayed or processed for that user.
     * @param {string} coordinationId - The `coordinationId` parameter in the `manageCookie` function
     * represents the unique identifier for a specific coordination item. It is used to save or update user
     * preferences related to coordination.
     * @param {string} coordinationItems - The `coordinationItems` parameter in the `manageCookie` function
     * likely refers to a list of items related to coordination. It is passed as a string in the function
     * along with other parameters such as `storeId`, `languageId`, `currencyId`, `coordinationId`,
     * `periodYear`,
     * @param {string} periodYear - The `periodYear` parameter in the `manageCookie` function represents
     * the year for which the user preferences are being managed or updated. It is a string type parameter
     * that specifies the year value.
     * @param {string} periodMonth - The `periodMonth` parameter in the `manageCookie` function represents
     * the month for which the user preferences are being managed. It is a string parameter that specifies
     * the month in the format required by the application, such as "01" for January, "02" for February,
     * and so on.
     * @returns The `manageCookie` function returns a Promise that resolves to a boolean value indicating
     * whether the user preferences were saved successfully. If the preferences were saved successfully, it
     * then retrieves the user preferences and returns them. If there was an error during the process, it
     * returns `null`.
     */
    async manageCookie(_userId: string, storeId: string, userId: string, languageId: string, currencyId: string, coordinationId: string,
        coordinationItems: string, periodYear: string, periodMonth: string, device: string): Promise<boolean> {
        try {
            // Save or update user preferences
            const preferencesSaved: boolean = await this.saveUpdateUserPreferences(_userId, storeId, userId, languageId, currencyId, coordinationId,
                coordinationItems, periodYear, periodMonth, device);

            // Check if preferences were saved successfully
            if (preferencesSaved) {
                const userPreferences = await this.getUserPreferences(_userId, storeId, userId, languageId, currencyId, coordinationId,
                    coordinationItems, periodYear, periodMonth, device);
                return userPreferences;
            } else {
                return null;
            }
        } catch (error) {
            return null;
        }
    }

    // OBTIENE , ACTUALIZA Y/O CREA COOKIE TOVPIL
    /**
     * The function `getSantovTovpilCookie` decodes a base64 encoded cookie value and returns it as a
     * `CookieTovpil` object.
     * @returns The `getSantovTovpilCookie()` function is returning the user object after decoding the
     * base64 data retrieved from the 'tovpilsantov' cookie using the `atob()` function. The user object is
     * then cast as type `CookieTovpil` before being returned.
     */
    async getSantovTovpilCookie() {
        let dataB64 = this.cookieService.get(Constants.cookie_Name);

        if (dataB64 != null && dataB64 != undefined) {
            let user = atob(dataB64);
            return JSON.parse(user) as CookieTovpil;
        } else {
            return null;
        }
    }

    /**
     * The function `updateTovpilCookie` updates a cookie with user preferences based on the provided
     * parameters.
     * @param {string} _userId - The `_userId` parameter in the `updateTovpilCookie` function is a string
     * that represents the user ID. It is used to update the user preferences stored in a cookie named
     * 'tovpilsantov'.
     * @param {string} _storeId - The `_storeId` parameter in the `updateTovpilCookie` function is used to
     * specify the store ID associated with the user preferences being updated in the cookie. It is a
     * string value that helps identify the specific store for which the user preferences are being
     * managed.
     * @param {string} _id - The `_id` parameter in the `updateTovpilCookie` function is used to specify an
     * identifier for the user preferences. It is a string value that helps uniquely identify the user
     * preferences stored in the cookie.
     * @param {string} _language - The `_language` parameter in the `updateTovpilCookie` function is used
     * to specify the language preference for a user. It is a string value that represents the language
     * code or identifier that the user has selected for their preferences. This parameter is used to
     * update the language setting in the user
     * @param {string} _selCurrency - The `_selCurrency` parameter in the `updateTovpilCookie` function is
     * used to specify the selected currency preference for a user. It is a string that represents the
     * currency code or symbol that the user has selected for their preferences.
     * @param {string} _coordinationId - The `_coordinationId` parameter in the `updateTovpilCookie`
     * function is used to store the coordination ID in the user preferences cookie. It is one of the
     * values that can be updated or set when updating the user preferences stored in the cookie.
     * @param {string} _coordinationStack - The `_coordinationStack` parameter in the `updateTovpilCookie`
     * function appears to be a string type parameter. It is used to store information related to
     * coordination stack in the user preferences cookie. This parameter is being updated or set in the
     * user preferences cookie based on the value passed to
     * @param {string} _selYear - The `_selYear` parameter in the `updateTovpilCookie` function is used to
     * specify the selected year in the user preferences for a cookie named 'tovpilsantov'. This parameter
     * allows the function to update or set the selected year value in the user preferences stored in the
     * @param {string} _selPeriod - The `_selPeriod` parameter in the `updateTovpilCookie` function is used
     * to specify a selected period in the user preferences for updating a cookie named 'tovpilsantov'.
     * This parameter likely represents a specific time period or duration that the user has selected as
     * part of their
     * @param {string} _device - The `_device` parameter in the `updateTovpilCookie` function is used to
     * specify the device information for user preferences. It could be the type of device the user is
     * using, such as "desktop", "mobile", "tablet", etc. This information can be stored in the cookie
     */
    async updateTovpilCookie(
        _levelId: string,
        _userId: string,
        _storeId: string,
        _id: string,
        _language: string,
        _selCurrency: string,
        _coordinationId: string,
        _coordinationStack: string,
        _selYear: string,
        _selPeriod: string,
        _device: string,

        _storeSelected: StoreSelected,
        _periodSelected: PeriodSelected,
        _stores: StoreSelected[],
    ) {
        console.debug("UserPreferences(): Se actualizará cookie tovpil ... ")
        let userPreferences: CookieTovpil = await this.getSantovTovpilCookie() || null;
        console.debug("Preferencias: ", userPreferences);

        if (userPreferences == null || userPreferences == undefined) {
            console.debug("UserPreferences(): Creando cookie tovpil ... ")
            const initialCookie: CookieTovpil = this.getDefaultPreferences();

            // SETEAMOS VALORES
            initialCookie.preferences.levelId = _levelId;
            initialCookie.preferences.userId = _userId;
            initialCookie.storeId = _storeId;
            initialCookie.preferences.id = _id;
            initialCookie.preferences.language = _language;
            initialCookie.preferences.selCurrency = _selCurrency;
            initialCookie.preferences.coordinationId = _coordinationId;
            initialCookie.preferences.coordinationStack = _coordinationStack;
            initialCookie.preferences.selYear = _selYear;
            initialCookie.preferences.selPeriod = _selPeriod;
            initialCookie.preferences.device = _device;

            this.tovpilCookieService.addObjectCookie(Constants.cookie_Name, initialCookie, 1);
        } else {
            console.debug("UserPreferences(): Actualizando cookie tovpil ... ");

            // SETEAMOS VALORES
            if (_levelId !== null) {
                userPreferences.preferences.levelId = _levelId;
            }
            if (_userId !== null) {
                userPreferences.preferences.userId = _userId;
            }
            if (_storeId !== null) {
                userPreferences.storeId = _storeId;
            }
            if (_id !== null) {
                userPreferences.preferences.id = _id;
            }
            if (_language !== null) {
                userPreferences.preferences.language = _language;
            }
            if (_selCurrency !== null) {
                userPreferences.preferences.selCurrency = _selCurrency;
            }
            if (_coordinationId !== null) {
                userPreferences.preferences.coordinationId = _coordinationId;
            }
            if (_coordinationStack !== null) {
                userPreferences.preferences.coordinationStack = _coordinationStack;
            }
            if (_selYear !== null) {
                userPreferences.preferences.selYear = _selYear;
            }
            if (_selPeriod !== null) {
                userPreferences.preferences.selPeriod = _selPeriod;
            }
            if (_device !== null) {
                userPreferences.preferences.device = _device;
            }

            if (_storeSelected !== null) {
                userPreferences.preferences.storeSelected = _storeSelected;
            }

            if (_periodSelected !== null) {
                userPreferences.preferences.periodSelected = _periodSelected;
            }

            if (_stores !== null) {
                userPreferences.preferences.stores = _stores;
            }

            console.debug("Preferencias a actualizar: ", userPreferences)
            this.tovpilCookieService.deleteCookie(Constants.cookie_Name);

            //this.cookieService.set(Constants.cookie_Name, content);
            this.tovpilCookieService.addObjectCookie(Constants.cookie_Name, userPreferences);
        }
    }

    /**
     * The function `getDefaultPreferences` returns a default set of preferences for a user, including
     * various properties and nested objects.
     * @returns The `getDefaultPreferences()` function returns a default object of type `CookieTovpil` with
     * various properties initialized to default values. The object includes properties such as `code`,
     * `accessToken`, `tokenType`, `login`, `name`, `lastName`, `localId`, `storeId`, `tokenProfile`, and
     * `preferences` which contains nested properties like `id`, `userId`, `guide
     */
    getDefaultPreferences(): CookieTovpil {
        return {
            code: '',
            accessToken: '',
            tokenType: '',
            login: '',
            name: '',
            lastName: '',
            localId: '',
            storeId: '',
            tokenProfile: '',
            preferences: {
                id: '',
                userId: '',
                guideId: '',
                fullName: '',
                usrSystemId: '',
                supplId: '',
                functionId: '',
                levelId: '',
                storeId: '',
                language: '',
                region: '',
                stateId: '',
                currDate: '',
                selYear: '',
                selPeriod: '',
                selCurrency: '',
                tokenProfile: '',
                coordinationId: '',
                coordinationStack: '',
                stores: [],
                storeSelected: {
                    id: '',
                    label: ''
                },
                periodSelected: {
                    year: '',
                    period: '',
                    descrip: ''
                },
                device: ''
            }
        };
    }
}

